import { useState, useEffect, useCallback } from 'react';
import { Activity, Download, Filter, Search, X } from 'lucide-react';
import { authenticatedFetch } from '../../lib/auth';

interface ActivityLogItem {
  id: string;
  timestamp: string;
  action_type: string;
  action_type_label: string;
  action: string;
  action_label: string;
  description: string;
  entity_type: string | null;
  entity_id: string | null;
  entity_name: string | null;
  user: { id: string; email: string; name: string; role: string | null } | null;
  ip_address: string | null;
  api: { username: string; endpoint: string; status: number; response_time_ms: number } | null;
  has_details: boolean;
  has_changes: boolean;
}

interface Stats {
  stats: Record<string, number>;
  labels: Record<string, string>;
  total: number;
}

interface UserOption {
  id: string;
  email: string;
  full_name: string;
  event_count: number;
}

const TYPE_COLORS: Record<string, string> = {
  auth: 'bg-purple-100 text-purple-800 border-purple-300',
  study_plan: 'bg-kf-blue-100 text-kf-blue-800 border-kf-blue-300',
  exam: 'bg-cyan-100 text-cyan-800 border-cyan-300',
  certificate: 'bg-emerald-100 text-emerald-800 border-emerald-300',
  member: 'bg-amber-100 text-amber-800 border-amber-300',
  lms_integration: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  external_credential: 'bg-pink-100 text-pink-800 border-pink-300',
  system: 'bg-red-100 text-red-800 border-red-300',
  api: 'bg-gray-100 text-gray-700 border-gray-300',
  misc: 'bg-slate-100 text-slate-700 border-slate-300'
};

const PAGE_SIZE = 50;

function fromDaysAgo(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

export default function ActivityLog() {
  const [items, setItems] = useState<ActivityLogItem[]>([]);
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  // Filters
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [excludeApi, setExcludeApi] = useState(true); // default skjul støy
  const [dateRange, setDateRange] = useState<number>(7); // dager
  const [userId, setUserId] = useState<string>('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Detail modal
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<any>(null);

  const buildQuery = useCallback(() => {
    const q = new URLSearchParams();
    let activeTypes = [...selectedTypes];
    if (excludeApi && activeTypes.length === 0) {
      // Default: skjul api-støy
      activeTypes = ['auth', 'study_plan', 'exam', 'certificate', 'member', 'lms_integration', 'external_credential', 'system', 'misc'];
    }
    if (activeTypes.length > 0) {
      q.set('action_type', activeTypes.join(','));
    }
    if (dateRange > 0) {
      q.set('from', fromDaysAgo(dateRange));
    }
    if (userId) q.set('user_id', userId);
    if (search) q.set('search', search);
    return q;
  }, [selectedTypes, excludeApi, dateRange, userId, search]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const baseQ = buildQuery();
      const listQ = new URLSearchParams(baseQ);
      listQ.set('limit', String(PAGE_SIZE));
      listQ.set('offset', String(page * PAGE_SIZE));

      const [listRes, statsRes] = await Promise.all([
        authenticatedFetch(`/api/admin/activity-log?${listQ.toString()}`),
        authenticatedFetch(`/api/admin/activity-log/stats?${baseQ.toString()}`)
      ]);
      const list = await listRes.json();
      const statsJson = await statsRes.json();
      setItems(list.items || []);
      setTotal(list.pagination?.total || 0);
      setStats(statsJson);
    } catch (err) {
      console.error('Load activity-log failed:', err);
    } finally {
      setLoading(false);
    }
  }, [buildQuery, page]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    authenticatedFetch('/api/admin/activity-log/users')
      .then(r => r.json())
      .then(d => setUsers(d.users || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!detailId) { setDetail(null); return; }
    authenticatedFetch(`/api/admin/activity-log/${detailId}`)
      .then(r => r.json())
      .then(setDetail)
      .catch(() => setDetail(null));
  }, [detailId]);

  function toggleType(type: string) {
    setExcludeApi(false); // bruker overstyrer
    setPage(0);
    setSelectedTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    setPage(0);
    setSearch(searchInput.trim());
  }

  function clearFilters() {
    setSelectedTypes([]);
    setUserId('');
    setSearch('');
    setSearchInput('');
    setExcludeApi(true);
    setDateRange(7);
    setPage(0);
  }

  async function handleExport() {
    const q = buildQuery();
    const url = `/api/admin/activity-log/export.csv?${q.toString()}`;
    const res = await authenticatedFetch(url);
    if (!res.ok) {
      alert('Eksport feilet');
      return;
    }
    const blob = await res.blob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `activity-log-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  }

  function formatTime(iso: string): string {
    const d = new Date(iso);
    const today = new Date();
    const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
    const sameDay = d.toDateString() === today.toDateString();
    const sameYesterday = d.toDateString() === yesterday.toDateString();
    const hhmm = d.toLocaleTimeString('nb', { hour: '2-digit', minute: '2-digit' });
    if (sameDay) return `i dag ${hhmm}`;
    if (sameYesterday) return `i går ${hhmm}`;
    return d.toLocaleDateString('nb', { day: '2-digit', month: 'short', year: 'numeric' }) + ' ' + hhmm;
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Activity size={22} className="text-kf-primary" />
            Aktivitetslogg
          </h2>
          <p className="text-sm text-gray-600 mt-1">
            Sentral oversikt over hendelser i systemet. Kun synlig for systemadmin.
          </p>
        </div>
        <button
          onClick={handleExport}
          className="flex items-center gap-2 px-3 py-2 bg-kf-primary text-white rounded-md text-sm hover:bg-kf-primary-dark"
        >
          <Download size={16} />
          Eksporter CSV
        </button>
      </div>

      {/* Kategori-filter med counts */}
      {stats && (
        <div className="mb-4">
          <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-1">
            <Filter size={12} />
            Kategori:
          </div>
          <div className="flex flex-wrap gap-2">
            {Object.entries(stats.labels).map(([type, label]) => {
              const count = stats.stats[type] || 0;
              const active = selectedTypes.includes(type);
              const dimmed = !active && selectedTypes.length > 0;
              return (
                <button
                  key={type}
                  onClick={() => toggleType(type)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium border ${TYPE_COLORS[type] || TYPE_COLORS.misc} ${active ? 'ring-2 ring-offset-1 ring-kf-primary' : ''} ${dimmed ? 'opacity-40' : ''}`}
                  title={`${label}: ${count} hendelser`}
                >
                  {label} <span className="ml-1 opacity-70">({count})</span>
                </button>
              );
            })}
            {(selectedTypes.length > 0 || userId || search) && (
              <button
                onClick={clearFilters}
                className="px-2 py-1 rounded-full text-xs text-gray-600 hover:bg-gray-100 flex items-center gap-1"
              >
                <X size={12} /> Tøm filtre
              </button>
            )}
          </div>
        </div>
      )}

      {/* Dato + bruker + søk */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        <select
          value={dateRange}
          onChange={(e) => { setDateRange(Number(e.target.value)); setPage(0); }}
          className="border rounded px-2 py-1.5 text-sm"
        >
          <option value={1}>Siste 24t</option>
          <option value={7}>Siste 7 dager</option>
          <option value={30}>Siste 30 dager</option>
          <option value={90}>Siste 90 dager</option>
          <option value={0}>Alle</option>
        </select>

        <select
          value={userId}
          onChange={(e) => { setUserId(e.target.value); setPage(0); }}
          className="border rounded px-2 py-1.5 text-sm max-w-xs"
        >
          <option value="">Alle brukere</option>
          {users.map(u => (
            <option key={u.id} value={u.id}>
              {u.full_name || u.email} ({u.event_count})
            </option>
          ))}
        </select>

        <form onSubmit={handleSearchSubmit} className="flex-1 max-w-md flex gap-1">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-2.5 top-2.5 text-gray-400" />
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Søk: handling, entity-id, e-post…"
              className="w-full pl-8 pr-2 py-1.5 border rounded text-sm"
            />
          </div>
          <button type="submit" className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded text-sm">
            Søk
          </button>
        </form>
      </div>

      {/* Hendelser-liste (én rad = én lesbar hendelse) */}
      <div className="border rounded-md divide-y divide-gray-100 bg-white">
        {loading && (
          <div className="px-4 py-10 text-center text-gray-500">Laster…</div>
        )}
        {!loading && items.length === 0 && (
          <div className="px-4 py-10 text-center text-gray-500">Ingen treff for valgte filtre.</div>
        )}
        {!loading && items.map(item => (
          <div key={item.id} className="px-4 py-2.5 hover:bg-gray-50 group">
            <div className="flex items-start gap-3">
              <div className="text-xs text-gray-500 whitespace-nowrap pt-0.5 w-32 flex-shrink-0 tabular-nums">
                {formatTime(item.timestamp)}
              </div>
              <span className={`inline-block px-2 py-0.5 rounded text-[10px] border whitespace-nowrap ${TYPE_COLORS[item.action_type] || TYPE_COLORS.misc} flex-shrink-0 mt-0.5`}>
                {item.action_type_label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-900">{item.description}</div>
                {(item.user || item.ip_address) && (
                  <div className="text-[11px] text-gray-500 mt-0.5">
                    {item.user && (
                      <span>{item.user.email}{item.user.role ? ` · ${item.user.role}` : ''}</span>
                    )}
                    {item.user && item.ip_address && ' · '}
                    {item.ip_address && <span className="font-mono">{item.ip_address}</span>}
                  </div>
                )}
              </div>
              {(item.has_details || item.has_changes) && (
                <button
                  onClick={() => setDetailId(item.id)}
                  className="text-xs text-kf-primary hover:underline opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap mt-0.5"
                >
                  Vis detaljer
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Paginering */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 text-sm">
          <div className="text-gray-600">
            Viser {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} av {total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              ← Forrige
            </button>
            <span className="px-3 py-1">Side {page + 1} av {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page + 1 >= totalPages}
              className="px-3 py-1 border rounded disabled:opacity-50"
            >
              Neste →
            </button>
          </div>
        </div>
      )}

      {/* Detail modal */}
      {detailId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white">
              <h3 className="text-lg font-semibold">Hendelse-detaljer</h3>
              <button onClick={() => setDetailId(null)} className="text-gray-500 hover:text-gray-700">
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-3">
              {detail ? (
                <>
                  <DetailRow label="Tidspunkt" value={new Date(detail.timestamp).toLocaleString('nb')} />
                  <DetailRow label="Kategori" value={detail.action_type_label} />
                  <DetailRow label="Handling" value={detail.action} mono />
                  {detail.entity_type && <DetailRow label="Entity" value={`${detail.entity_type} ${detail.entity_id || ''}`} mono />}
                  {detail.user && <DetailRow label="Bruker" value={`${detail.user.name || ''} <${detail.user.email}> (${detail.user.role || 'ukjent'})`} />}
                  {detail.ip_address && <DetailRow label="IP" value={detail.ip_address} mono />}
                  {detail.api && <DetailRow label="API" value={`${detail.api.username} → ${detail.api.endpoint} (status ${detail.api.status}, ${detail.api.response_time_ms}ms)`} mono />}
                  {detail.user_agent && <DetailRow label="User-Agent" value={detail.user_agent.slice(0, 200)} mono />}
                  {detail.details && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Detaljer:</div>
                      <pre className="bg-gray-50 border rounded p-2 text-xs overflow-x-auto max-h-64">
                        {JSON.stringify(detail.details, null, 2)}
                      </pre>
                    </div>
                  )}
                  {detail.old_values && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Før:</div>
                      <pre className="bg-red-50 border border-red-200 rounded p-2 text-xs overflow-x-auto max-h-64">
                        {JSON.stringify(detail.old_values, null, 2)}
                      </pre>
                    </div>
                  )}
                  {detail.new_values && (
                    <div>
                      <div className="text-xs font-semibold text-gray-600 mb-1">Etter:</div>
                      <pre className="bg-green-50 border border-green-200 rounded p-2 text-xs overflow-x-auto max-h-64">
                        {JSON.stringify(detail.new_values, null, 2)}
                      </pre>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center text-gray-500 py-8">Laster…</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex gap-2">
      <div className="text-xs font-semibold text-gray-600 w-32 flex-shrink-0">{label}:</div>
      <div className={`text-sm text-gray-900 ${mono ? 'font-mono text-xs' : ''} break-all`}>{value}</div>
    </div>
  );
}
